import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UnifiedAuthContext = createContext();

export const useUnifiedAuth = () => {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error('useUnifiedAuth must be used within a UnifiedAuthProvider');
  }
  return context;
};

export const UnifiedAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(localStorage.getItem('sessionToken'));
  
  // Configure axios default headers
  useEffect(() => {
    if (authToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [authToken]);

  const checkUserRole = async () => {
    try {
      const token = localStorage.getItem('sessionToken');
      if (!token) {
        setUser(null);
        setUserRole('patient'); // default
        return;
      }

      // Use unified role endpoint
      const roleResponse = await axios.get(`${BACKEND_URL}/api/user/role`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (roleResponse.data) {
        setUser(roleResponse.data);
        
        // Determine role display based on user type
        const userType = roleResponse.data.user_type || roleResponse.data.role;
        let displayRole = 'Patient'; // default
        
        if (userType === 'doctor') {
          displayRole = 'Doctor';
        } else if (userType === 'provider') {
          displayRole = 'Provider';
        } else if (userType === 'admin') {
          displayRole = 'Admin';
        }
        
        setUserRole(displayRole);
        
        console.log('✅ Unified Auth - User role set:', {
          userType,
          displayRole,
          userData: roleResponse.data
        });
      }
      
    } catch (error) {
      console.error('❌ Unified Auth - Role check failed:', error);
      // On error, clear auth state
      localStorage.removeItem('sessionToken');
      setAuthToken(null);
      setUser(null);
      setUserRole('Patient');
    }
  };

  const login = async (sessionData) => {
    try {
      console.log('🔐 Unified Auth - Starting login with:', sessionData);
      
      const sessionToken = sessionData.session_token;
      if (!sessionToken) {
        throw new Error('No session token received');
      }

      // Store token
      localStorage.setItem('sessionToken', sessionToken);
      setAuthToken(sessionToken);

      // Native apps only — register the device for push notifications.
      // No-op on web (handled by the existing VAPID web-push flow).
      try {
        // Lazy import keeps web bundle smaller and avoids SSR surprises.
        import('../utils/nativeBridge').then(({ isNative, registerPushNotifications, secureStorage }) => {
          if (isNative()) {
            secureStorage.set('sessionToken', sessionToken);
            registerPushNotifications(sessionToken).catch(() => {});
          }
        });
      } catch (_) { /* noop */ }
      
      // Set user data if provided
      if (sessionData.user) {
        setUser(sessionData.user);
        
        // Set role based on user type
        const userType = sessionData.user.user_type;
        let displayRole = 'Patient';
        
        if (userType === 'doctor') {
          displayRole = 'Doctor';
        } else if (userType === 'provider') {
          displayRole = 'Provider';
        } else if (userType === 'admin') {
          displayRole = 'Admin';
        }
        
        setUserRole(displayRole);
        
        console.log('✅ Unified Auth - Login successful:', {
          userType,
          displayRole,
          user: sessionData.user
        });
      } else {
        // If no user data provided, fetch it
        await checkUserRole();
      }

      return true;
      
    } catch (error) {
      console.error('❌ Unified Auth - Login failed:', error);
      logout();
      return false;
    }
  };

  const logout = async () => {
    try {
      console.log('🔓 Unified Auth - Logging out');
      
      // Clear local storage
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userName');
      
      // Clear state
      setAuthToken(null);
      setUser(null);
      setUserRole('Patient');
      
      // Clear axios header
      delete axios.defaults.headers.common['Authorization'];
      
      console.log('✅ Unified Auth - Logout complete');
      
      // Reload page to reset all state
      window.location.reload();
      
    } catch (error) {
      console.error('❌ Unified Auth - Logout error:', error);
      // Force reload anyway
      window.location.reload();
    }
  };

  const demoLogin = async (demoType = 'patient') => {
    try {
      console.log('🎭 Unified Auth - Demo login as:', demoType);
      
      let sessionId;
      if (demoType === 'doctor') {
        sessionId = 'demo-session-doctor';
      } else if (demoType === 'provider') {
        sessionId = 'demo-session-provider';
      } else if (demoType === 'user' || demoType === 'patient') {
        // Handle both 'user' from frontend and 'patient' as fallback
        sessionId = 'demo-session-patient';
      } else {
        sessionId = 'demo-session-patient'; // default fallback
      }

      console.log(`🔗 Unified Auth - Using session ID: ${sessionId}`);

      // Call backend session creation
      const response = await axios.post(`${BACKEND_URL}/api/auth/session`, {}, {
        headers: { 'X-Session-ID': sessionId }
      });

      if (response.data && response.data.session_token) {
        console.log('✅ Unified Auth - Backend session created:', {
          sessionToken: response.data.session_token.substring(0, 8) + '...',
          userType: response.data.user?.user_type,
          userName: response.data.user?.name
        });
        
        const success = await login(response.data);
        return success;
      } else {
        throw new Error('Invalid session response');
      }
      
    } catch (error) {
      console.error('❌ Unified Auth - Demo login failed:', error);
      return false;
    }
  };

  // Initialize auth state on component mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        setLoading(true);
        
        const token = localStorage.getItem('sessionToken');
        if (token) {
          setAuthToken(token);
          await checkUserRole();
        } else {
          setUser(null);
          setUserRole('Patient');
        }
        
      } catch (error) {
        console.error('❌ Unified Auth - Init error:', error);
        setUser(null);
        setUserRole('Patient');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Monitor token changes
  useEffect(() => {
    if (authToken && !user) {
      checkUserRole();
    }
  }, [authToken, user]);

  const value = {
    user,
    userRole,
    loading,
    login,
    logout,
    demoLogin,
    checkUserRole,
    isAuthenticated: !!user,
    authToken,
    sessionToken: authToken  // For backward compatibility
  };

  return (
    <UnifiedAuthContext.Provider value={value}>
      {children}
    </UnifiedAuthContext.Provider>
  );
};

export default UnifiedAuthProvider;